import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import {
  X, CheckCircle, XCircle, Loader2, Send, ImageOff, Lightbulb, ListChecks,
  Boxes, Link2, AlertTriangle, Gauge, Code2, Database, Layers, LayoutTemplate,
  ArrowRight, MessageSquare, Copy, Check, RotateCcw,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  usePipelineApprove, usePipelineReject, usePipelineMessage, usePipelineRerun,
} from '@/hooks/useApi'
import type { PipelineStageOutput } from '@/lib/api'

// --- Shared markdown renderer, matching ChatPanel's established dark-theme styling exactly ---

function CodeBlock({ code, language }: { code: string; language: string }) {
  return (
    <div className="my-3 rounded-lg overflow-hidden border border-white/10">
      <div className="px-3 py-1.5 bg-white/5 text-[10px] text-text-muted font-mono border-b border-white/10">{language}</div>
      <SyntaxHighlighter style={oneDark} language={language} PreTag="div" customStyle={{ margin: 0, borderRadius: 0, fontSize: '12px', whiteSpace: 'pre-wrap', overflowX: 'auto', maxWidth: '100%' }}>
        {code}
      </SyntaxHighlighter>
    </div>
  )
}

function MarkdownContent({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        code({ className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '')
          const codeStr = String(children).replace(/\n$/, '')
          if (match) return <CodeBlock code={codeStr} language={match[1]} />
          return <code className="px-1.5 py-0.5 rounded bg-white/5 text-xs font-mono text-accent" {...props}>{children}</code>
        },
        pre({ children }) { return <>{children}</> },
        h1({ children }) { return <h1 className="text-xl font-bold mt-6 mb-3 pb-2 border-b border-white/10 text-text-primary">{children}</h1> },
        h2({ children }) { return <h2 className="text-lg font-semibold mt-5 mb-2 text-text-primary">{children}</h2> },
        h3({ children }) { return <h3 className="text-base font-semibold mt-4 mb-2 text-text-primary">{children}</h3> },
        h4({ children }) { return <h4 className="text-sm font-semibold mt-3 mb-1 text-text-secondary">{children}</h4> },
        p({ children }) { return <p className="mb-3 last:mb-0 leading-relaxed">{children}</p> },
        ul({ children }) { return <ul className="list-disc pl-5 mb-3 space-y-1">{children}</ul> },
        ol({ children }) { return <ol className="list-decimal pl-5 mb-3 space-y-1">{children}</ol> },
        li({ children }) { return <li className="leading-relaxed">{children}</li> },
        table({ children }) {
          return <div className="overflow-x-auto my-3 rounded-lg border border-white/10"><table className="text-xs border-collapse w-full">{children}</table></div>
        },
        th({ children }) { return <th className="border-b border-white/10 px-3 py-2 bg-white/5 text-left font-semibold text-text-primary">{children}</th> },
        td({ children }) { return <td className="border-b border-white/5 px-3 py-2 text-text-secondary">{children}</td> },
        blockquote({ children }) { return <blockquote className="border-l-3 border-accent/50 pl-4 my-3 text-text-muted italic bg-accent/5 py-2 pr-3 rounded-r-lg">{children}</blockquote> },
        hr() { return <hr className="my-4 border-white/10" /> },
        strong({ children }) { return <strong className="font-semibold text-text-primary">{children}</strong> },
      }}
    >
      {content}
    </ReactMarkdown>
  )
}

// --- Copy-to-clipboard, used on the output and review sections ---

export function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  if (!text) return null
  return (
    <button
      onClick={(e) => {
        e.stopPropagation()
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      }}
      className="shrink-0 p-1 rounded hover:bg-surface-elevated text-text-muted hover:text-text-primary transition-colors"
      title="Copy to clipboard"
    >
      {copied ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3" />}
    </button>
  )
}

// --- Small structural building blocks ---

function Section({ icon: Icon, title, action, children }: { icon: React.ComponentType<{ className?: string }>; title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="h-3.5 w-3.5 text-accent" />
          <span className="text-xs font-semibold text-text-primary uppercase tracking-wide">{title}</span>
        </div>
        {action}
      </div>
      {children}
    </div>
  )
}

function ComplexityBadge({ level }: { level: string }) {
  const map: Record<string, string> = {
    low: 'bg-green-500/15 text-green-400 border-green-500/30',
    medium: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    high: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
    very_high: 'bg-red-500/15 text-red-400 border-red-500/30',
  }
  return <Badge className={`text-[10px] border ${map[level] ?? 'bg-surface text-text-muted border-border'}`}><Gauge className="h-2.5 w-2.5 mr-1" />{level.replace('_', ' ')} complexity</Badge>
}

// --- Illustration image ---

function IllustrationImage({ imageId }: { imageId: string }) {
  const [failed, setFailed] = useState(false)
  if (failed) {
    return (
      <div className="flex items-center gap-2 px-4 py-6 rounded-lg border border-dashed border-border text-text-muted text-xs justify-center">
        <ImageOff className="h-4 w-4" /> Illustration could not be loaded ({imageId})
      </div>
    )
  }
  return (
    <img
      src={`/api/dashboard/images/${encodeURIComponent(imageId)}`}
      alt="AI-generated concept illustration for this stage"
      className="w-full max-h-80 object-cover rounded-lg border border-border"
      onError={() => setFailed(true)}
    />
  )
}

// --- Per-field structured renderers, one per known JSON shape ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function PlanningFields({ data }: { data: Record<string, any> }) {
  return (
    <div className="space-y-5">
      {data.estimated_complexity && <ComplexityBadge level={String(data.estimated_complexity)} />}

      {Array.isArray(data.requirements) && data.requirements.length > 0 && (
        <Section icon={ListChecks} title="Requirements">
          <ul className="space-y-1.5">
            {data.requirements.map((r: string, i: number) => (
              <li key={i} className="text-sm text-text-secondary flex gap-2"><span className="text-accent shrink-0">•</span>{r}</li>
            ))}
          </ul>
        </Section>
      )}

      {Array.isArray(data.architecture_decisions) && data.architecture_decisions.length > 0 && (
        <Section icon={Lightbulb} title="Architecture decisions">
          <div className="space-y-2">
            {data.architecture_decisions.map((d: { decision: string; reason: string }, i: number) => (
              <div key={i} className="rounded-lg border border-border bg-surface p-3">
                <p className="text-sm text-text-primary font-medium">{d.decision}</p>
                <p className="text-xs text-text-muted mt-1">{d.reason}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(data.components) && data.components.length > 0 && (
        <Section icon={Boxes} title="Components">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="text-xs w-full border-collapse">
              <thead><tr className="bg-surface-elevated">
                <th className="px-3 py-2 text-left font-semibold text-text-primary border-b border-border">Name</th>
                <th className="px-3 py-2 text-left font-semibold text-text-primary border-b border-border">Type</th>
                <th className="px-3 py-2 text-left font-semibold text-text-primary border-b border-border">Purpose</th>
                <th className="px-3 py-2 text-left font-semibold text-text-primary border-b border-border">New?</th>
              </tr></thead>
              <tbody>
                {data.components.map((c: { name: string; type: string; purpose: string; new_or_existing: string }, i: number) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-mono text-text-primary">{c.name}</td>
                    <td className="px-3 py-2 text-text-secondary">{c.type}</td>
                    <td className="px-3 py-2 text-text-secondary">{c.purpose}</td>
                    <td className="px-3 py-2"><Badge className={`text-[9px] border ${c.new_or_existing === 'new' ? 'bg-accent/15 text-accent border-accent/30' : 'bg-surface text-text-muted border-border'}`}>{c.new_or_existing}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {Array.isArray(data.dependencies) && data.dependencies.length > 0 && (
        <Section icon={Link2} title="Dependencies">
          <div className="flex flex-wrap gap-1.5">
            {data.dependencies.map((d: string, i: number) => (
              <span key={i} className="text-xs px-2 py-1 rounded-full bg-surface border border-border text-text-secondary">{d}</span>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(data.risks) && data.risks.length > 0 && (
        <Section icon={AlertTriangle} title="Risks">
          <div className="space-y-2">
            {data.risks.map((r: { risk: string; mitigation: string }, i: number) => (
              <div key={i} className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                <p className="text-sm text-text-primary"><span className="text-amber-400 font-medium">Risk:</span> {r.risk}</p>
                <p className="text-xs text-text-muted mt-1"><span className="text-text-secondary font-medium">Mitigation:</span> {r.mitigation}</p>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function DesignFields({ data }: { data: Record<string, any> }) {
  return (
    <div className="space-y-5">
      {Array.isArray(data.api_design) && data.api_design.length > 0 && (
        <Section icon={Code2} title="API design">
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="text-xs w-full border-collapse">
              <thead><tr className="bg-surface-elevated">
                <th className="px-3 py-2 text-left font-semibold text-text-primary border-b border-border">Method</th>
                <th className="px-3 py-2 text-left font-semibold text-text-primary border-b border-border">Path</th>
                <th className="px-3 py-2 text-left font-semibold text-text-primary border-b border-border">Worker</th>
                <th className="px-3 py-2 text-left font-semibold text-text-primary border-b border-border">Response</th>
              </tr></thead>
              <tbody>
                {data.api_design.map((a: { method: string; path: string; worker: string; response: string }, i: number) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    <td className="px-3 py-2"><Badge className="text-[9px] border bg-accent/15 text-accent border-accent/30 font-mono">{a.method}</Badge></td>
                    <td className="px-3 py-2 font-mono text-text-primary">{a.path}</td>
                    <td className="px-3 py-2 text-text-secondary">{a.worker}</td>
                    <td className="px-3 py-2 text-text-secondary">{a.response}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {Array.isArray(data.data_model_changes) && data.data_model_changes.length > 0 && (
        <Section icon={Database} title="Data model changes">
          <div className="space-y-2">
            {data.data_model_changes.map((d: { table: string; change: string; sql: string }, i: number) => (
              <div key={i} className="rounded-lg border border-border bg-surface p-3">
                <p className="text-sm text-text-primary"><span className="font-mono text-accent">{d.table}</span> — {d.change}</p>
                {d.sql && <CodeBlock code={d.sql} language="sql" />}
              </div>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(data.worker_changes) && data.worker_changes.length > 0 && (
        <Section icon={Layers} title="Worker changes">
          <div className="space-y-2">
            {data.worker_changes.map((w: { worker: string; changes: string; new_or_existing: string }, i: number) => (
              <div key={i} className="rounded-lg border border-border bg-surface p-3 flex items-start gap-2">
                <Badge className={`text-[9px] border shrink-0 ${w.new_or_existing === 'new' ? 'bg-accent/15 text-accent border-accent/30' : 'bg-surface-elevated text-text-muted border-border'}`}>{w.new_or_existing}</Badge>
                <div><p className="text-sm text-text-primary font-mono">{w.worker}</p><p className="text-xs text-text-muted mt-0.5">{w.changes}</p></div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(data.ui_changes) && data.ui_changes.length > 0 && (
        <Section icon={LayoutTemplate} title="UI changes">
          <div className="space-y-2">
            {data.ui_changes.map((u: { component: string; changes: string }, i: number) => (
              <div key={i} className="rounded-lg border border-border bg-surface p-3">
                <p className="text-sm text-text-primary font-medium">{u.component}</p>
                <p className="text-xs text-text-muted mt-1">{u.changes}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(data.sequence) && data.sequence.length > 0 && (
        <Section icon={ArrowRight} title="Interaction sequence">
          <ol className="space-y-1.5">
            {data.sequence.map((s: string, i: number) => (
              <li key={i} className="text-sm text-text-secondary flex gap-2">
                <span className="shrink-0 h-5 w-5 rounded-full bg-accent/15 text-accent text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
        </Section>
      )}
    </div>
  )
}

// --- Main detail view ---

export function StageDetailView({
  itemTitle, stage, onClose,
}: {
  itemTitle: string
  stage: PipelineStageOutput
  onClose: () => void
}) {
  const [note, setNote] = useState('')
  const [msgText, setMsgText] = useState('')
  const approve = usePipelineApprove()
  const reject = usePipelineReject()
  const sendMsg = usePipelineMessage()
  const rerun = usePipelineRerun()

  const data = stage.output_parsed as Record<string, unknown> | null
  const canReview = stage.status === 'awaiting_review'
  const bodyText = (data?.plan_text ?? data?.design_text ?? data?.structured_writeup) as string | undefined
  const illustrationId = data?.illustration_image_id as string | null | undefined
  const illustrationError = data?.illustration_error as string | undefined

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-stretch justify-end">
      <div className="w-full sm:max-w-2xl bg-surface h-full overflow-y-auto shadow-2xl flex flex-col">
        {/* Header -- stays anchored to the pipeline item, not a separate part of the dashboard */}
        <div className="sticky top-0 z-10 bg-surface border-b border-border px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] text-text-muted uppercase tracking-wide mb-0.5">Automation Pipeline &middot; {itemTitle}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-text-primary capitalize">{stage.stage}</h2>
              <Badge className="text-[10px] border bg-surface-elevated text-text-muted border-border">{stage.status.replace('_', ' ')}</Badge>
              <Badge className="text-[10px] border bg-surface-elevated text-text-muted border-border">{stage.engine}</Badge>
            </div>
          </div>
          <button onClick={onClose} className="shrink-0 p-1.5 rounded hover:bg-surface-elevated text-text-muted hover:text-text-primary transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-6">
          {stage.status === 'running' && (
            <div className="flex items-center gap-2 text-sm text-text-muted py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Still generating — check back shortly.
            </div>
          )}

          {stage.status === 'failed' && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-400">
              This run failed. {stage.output_parsed?.error ? String(stage.output_parsed.error) : 'No error detail was captured.'}
            </div>
          )}

          {data && !data.parse_error && (
            <>
              {illustrationId && <IllustrationImage imageId={illustrationId} />}
              {illustrationError && (
                <div className="text-xs text-text-muted flex items-center gap-1.5"><ImageOff className="h-3 w-3" /> Illustration generation failed: {illustrationError}</div>
              )}

              {stage.stage === 'planning' && <PlanningFields data={data} />}
              {stage.stage === 'design' && <DesignFields data={data} />}

              {bodyText && (
                <Section icon={MessageSquare} title="Full write-up" action={<CopyButton text={bodyText} />}>
                  <div className="text-sm text-text-secondary bg-surface-elevated/50 rounded-lg p-4 border border-border">
                    <MarkdownContent content={bodyText} />
                  </div>
                </Section>
              )}
            </>
          )}

          {/* Discussion thread */}
          {stage.conversations && stage.conversations.length > 0 && (
            <Section
              icon={MessageSquare}
              title="Discussion"
              action={<CopyButton text={stage.conversations.map((c) => `[${c.role}] ${c.content}`).join('\n\n---\n\n')} />}
            >
              <div className="space-y-2">
                {stage.conversations.map((c) => (
                  <div key={c.id} className={`text-xs rounded-lg px-3 py-2 ${c.role === 'user' ? 'bg-accent/10 border border-accent/20 text-text-primary ml-4' : 'bg-surface border border-border text-text-secondary'}`}>
                    <span className="text-[10px] text-text-muted mr-2">{c.role}</span>{c.content}
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* Review controls, sticky to the bottom, same as the compact card's pattern */}
        {canReview && (
          <div className="sticky bottom-0 bg-surface border-t border-border p-4 space-y-2">
            <div className="flex gap-2">
              <input
                className="flex-1 text-xs bg-surface-elevated border border-border rounded px-3 py-2 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
                placeholder="Discuss before approving..."
                value={msgText}
                onChange={(e) => setMsgText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && msgText.trim()) { sendMsg.mutate({ id: stage.pipeline_item_id, message: msgText.trim() }); setMsgText('') } }}
              />
              <button
                className="p-2 rounded border border-border bg-surface hover:bg-surface-elevated text-text-muted hover:text-text-primary transition-colors disabled:opacity-40"
                onClick={() => { if (msgText.trim()) { sendMsg.mutate({ id: stage.pipeline_item_id, message: msgText.trim() }); setMsgText('') } }}
                disabled={sendMsg.isPending || !msgText.trim()}
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                className="flex-1 min-w-[140px] text-xs bg-surface-elevated border border-border rounded px-3 py-2 text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
                placeholder="Approval note (optional)..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <button
                className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 transition-colors disabled:opacity-40"
                onClick={() => approve.mutate({ id: stage.pipeline_item_id, note: note || undefined })}
                disabled={approve.isPending}
              >
                {approve.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
                Approve
              </button>
              <button
                className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors disabled:opacity-40"
                onClick={() => reject.mutate({ id: stage.pipeline_item_id, message: note.trim() || 'Rejected — needs revision' })}
                disabled={reject.isPending}
              >
                {reject.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
                Reject
              </button>
              <button
                className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 transition-colors disabled:opacity-40"
                onClick={() => rerun.mutate({ id: stage.pipeline_item_id, stage: stage.stage, note: note.trim() || undefined })}
                disabled={rerun.isPending}
                title="Attach the note above (if any) and generate a fresh attempt at this stage -- doesn't approve or reject this one"
              >
                {rerun.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                Re-review with notes
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
